import uuid from 'uuid/v4';
import forEach from 'lodash/forEach';
import reduce from 'lodash/reduce';
import sortBy from 'lodash/sortBy';
import { RouteType } from '@Pages/RetroSynthesis/constants';
import { sortStepsByMajor, sortStepsByMinor } from '@Pages/RetroSynthesis/ProjectRoute/utils/stepSorters';
import { trimUptoTwoDecimals } from '../../utils';
import { buildStepsDataFromRouteLayout } from './stepsDataBuilder';

const _buildExamples = (examples) => {
  return (
    examples &&
    examples.map((example, i) => ({
      ...example,
      score: trimUptoTwoDecimals(example.score), // trim the confidence score to 2 decimal digits
      exampleNo: i + 1,
    }))
  );
};

const _buildStep = ({ step, type, reactionId, databaseId, examples, selected, parentId }) => {
  return {
    step,
    type,
    selected,
    ...(reactionId && { reactionId }),
    ...(databaseId && { databaseId }),
    ...(parentId && { parentId }),
    ...(examples && {
      examples: _buildExamples(examples),
    }),
    requested: false,
    isLoading: undefined,
    data: undefined,
  };
};

export function buildStepsData(payload) {
  const { routeType } = payload || {};

  switch (routeType) {
    case RouteType.PUBLISHED: {
      // eslint-disable-next-line no-use-before-define
      return buildConditionsStepsFromRouteLayoutResponse(payload);
    }
    case RouteType.PREDICTED: {
      // eslint-disable-next-line no-use-before-define
      return buildExamplesStepsFromRouteLayoutResponse(payload);
    }
    case RouteType.CUSTOMIZED:
    case RouteType.COMBINED: {
      // eslint-disable-next-line no-use-before-define
      return buildCustomizedStepsFromRouteLayoutResponse(payload);
    }
    default: {
      Logger.warn('Invalid routeType', routeType);
      return [];
    }
  }
}

/* Conditions */
export function buildConditionsStepsFromRouteLayoutResponse({ routeLayout }) {
  return reduce(
    routeLayout,
    (memo, { step, type, reaction, selected, parentId }) => {
      if (!step || !reaction) {
        return memo;
      }

      const { reactionId, databaseId } = reaction;
      memo.push(_buildStep({ step, type, selected, reactionId, databaseId, parentId }));

      return memo;
    },
    []
  );
}

// Map to make it work with SynthesisPlannerConditionsComponent
export function buildConditionsForStep(response, step) {
  const mapped = [];

  forEach(response, (reactions, databaseId) => {
    forEach(reactions, (conditions, reactionId) => {
      mapped.push({
        [reactionId]: conditions.map((prep) => ({
          ...prep,
          databaseId,
        })),
        step,
      });
    });
  });

  return { conditions: mapped };
}

/* Reaxys Examples */
export function buildExamplesStepsFromRouteLayoutResponse({ routeLayout }) {
  return reduce(
    routeLayout,
    (memo, { step, type, examples, selected, parentId }) => {
      if (!step || !examples) {
        return memo;
      }

      memo.push(_buildStep({ step, type, examples, selected, parentId }));

      return memo;
    },
    []
  );
}

export const buildRequestPayloadForExamplesSearch = ({ requestId, searchId, reactionIds = [], databaseId }) => {
  return {
    queryFactItem: {
      id: 'root',
      facts: [
        {
          id: 'ADVANCED.QL',
          exist: false,
          advancedItems: [
            {
              advancedExpression: `RX.ID=${reactionIds.join(';')}`,
              databaseId,
            },
          ],
        },
      ],
    },
    context: 'reactions',
    databases: [databaseId],
    searchId: searchId || uuid(),
    requestId: requestId || uuid(),
  };
};

export function buildCustomizedStepsFromRouteLayoutResponse({ routeLayout = [] }) {
  const steps = buildStepsDataFromRouteLayout(routeLayout, _buildStep);
  return sortBy(steps, [sortStepsByMajor, sortStepsByMinor]);
}
